import React, { useEffect, useRef, useState } from "react";
import { Check } from "lucide-react";
import { useLang, useT } from "../context/LanguageContext";

const PricingSection = () => {
  const t = useT();
  const { lang } = useLang();
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);

  const isTr = lang === "tr";

  const paygoFeatures = [
    t("pricing.plan.paygo.feature1"),
    t("pricing.plan.paygo.feature2"),
    t("pricing.plan.paygo.feature3"),
    t("pricing.plan.paygo.feature4"),
    t("pricing.plan.paygo.feature5"),
    t("pricing.plan.paygo.feature6"),
  ];

  const customFeatures = [
    t("pricing.plan.custom.feature1"),
    t("pricing.plan.custom.feature2"),
    t("pricing.plan.custom.feature3"),
    t("pricing.plan.custom.feature4"),
    t("pricing.plan.custom.feature5"),
    t("pricing.plan.custom.feature6"),
    t("pricing.plan.custom.feature7"),
  ];

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([e]) => e.isIntersecting && (setVisible(true), obs.disconnect()),
      { threshold: 0.1 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  return (
    <section id="pricing" ref={ref} className={`section-fade ${visible ? "visible" : ""} py-28 px-6`} style={{ background: "var(--section-surface-4)" }}>
      <div className="max-w-5xl mx-auto">
        <div className="flex flex-col items-center text-center mb-14">
          <span
            className="inline-block px-3 py-1 rounded-full text-[11px] font-semibold uppercase tracking-wider mb-4"
            style={{ background: "var(--card-bg)", border: "1px solid var(--border)", color: "var(--muted)" }}
          >
            {t("pricing.eyebrow")}
          </span>
          <h2 className="font-bold tracking-tight" style={{ color: "var(--fg)", fontSize: "clamp(2rem, 4.2vw, 3.25rem)" }}>
            {t("pricing.title")}
          </h2>
          <p className="mt-4 max-w-2xl text-[15px]" style={{ color: "var(--fg)", opacity: 0.92 }}>
            {t("pricing.subtitle")}
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-12">
          {/* PAYGO Card */}
          <div
            className="relative p-8 rounded-2xl transition-all duration-300 neon-box"
            style={{
              background: "var(--card-bg)",
              color: "var(--fg)",
              border: "1px solid var(--border)",
            }}
          >
            <span
              className="inline-block px-2.5 py-1 rounded text-[10px] font-bold uppercase tracking-wider mb-4"
              style={{ background: "var(--fg)", color: "var(--bg)" }}
            >
              {t("pricing.plan.paygo.badge")}
            </span>
            <h3 className="text-[22px] font-bold mb-1">{t("pricing.plan.paygo.name")}</h3>
            <p className="text-[13px] mb-6" style={{ color: "var(--muted)" }}>
              {t("pricing.plan.paygo.description")}
            </p>

            <div className="flex items-baseline gap-1 mb-6">
              <span className="text-[28px] font-bold tracking-tight" style={{ color: "var(--fg)" }}>
                {isTr ? "Kullanım Fiyatı" : "Usage Cost"}
              </span>
            </div>

            <a
              href="https://app.verasist.ai"
              className="w-full py-3 rounded-full font-semibold text-[14px] transition-all duration-200 mb-7 inline-block text-center neon-btn"
            >
              {t("pricing.plan.paygo.cta")}
            </a>

            <p
              className="text-[12px] font-semibold uppercase tracking-wider mb-3"
              style={{ color: "var(--muted)" }}
            >
              {t("pricing.featuresTitle")}
            </p>
            <div className="space-y-3">
              {paygoFeatures.map((feat) => (
                <div key={feat} className="flex items-start gap-2.5">
                  <Check
                    size={16}
                    className="mt-0.5 flex-shrink-0"
                    style={{ color: "#10b981" }}
                  />
                  <span className="text-[13.5px]" style={{ color: "var(--fg)" }}>
                    {feat}
                  </span>
                </div>
              ))}
            </div>
            <a
              href="/pricing"
              className="inline-flex items-center gap-1 text-[13px] font-medium mt-2"
              style={{ color: "var(--brand-cyan)" }}
            >
              {t("pricing.detail")}
            </a>
          </div>

          {/* Custom/Enterprise Card */}
          <div
            className="relative p-8 rounded-2xl transition-all duration-300 neon-box"
            style={{
              background: "var(--card-bg)",
              color: "var(--fg)",
              border: "2px solid var(--brand-cyan)",
            }}
          >
            <span
              className="inline-block px-2.5 py-1 rounded text-[10px] font-bold uppercase tracking-wider mb-4"
              style={{ background: "var(--brand-navy)", color: "#ffffff" }}
            >
              {t("pricing.plan.custom.badge")}
            </span>
            <h3 className="text-[22px] font-bold mb-1">{t("pricing.plan.custom.name")}</h3>
            <p className="text-[13px] mb-6" style={{ color: "var(--muted)" }}>
              {t("pricing.plan.custom.description")}
            </p>

            <div className="flex items-baseline gap-1 mb-6">
              <span className="text-[28px] font-bold tracking-tight" style={{ color: "var(--fg)" }}>
                {isTr ? "Özel Fiyat" : "Custom"}
              </span>
            </div>

            <a
              href={
                isTr
                  ? `mailto:hello@verasist.ai?subject=${encodeURIComponent("Kurumsal Fiyatlandırma Görüşmesi Talebi")}&body=${encodeURIComponent("Merhaba Verasist Ekibi,\n\nKurumsal çözümleriniz hakkında bilgi almak ve özel fiyatlandırma için bir görüşme planlamak istiyorum.\n\nŞirket:\nTelefon:\nUygun olduğum gün/saat:\n\nTeşekkürler,")}`
                  : `mailto:hello@verasist.ai?subject=${encodeURIComponent("Enterprise Pricing Discussion Request")}&body=${encodeURIComponent("Hello Verasist Team,\n\nI would like to learn more about your enterprise solutions and schedule a call to discuss custom pricing.\n\nCompany:\nPhone:\nPreferred day/time:\n\nThanks,")}`
              }
              className="w-full py-3 rounded-full font-semibold text-[14px] transition-all duration-200 mb-7 inline-block text-center neon-btn neon-btn-coral"
            >
              {t("pricing.plan.custom.cta")}
            </a>

            <p
              className="text-[12px] font-semibold uppercase tracking-wider mb-3"
              style={{ color: "var(--muted)" }}
            >
              {t("pricing.featuresTitle")}
            </p>
            <div className="space-y-3">
              {customFeatures.map((feat) => (
                <div key={feat} className="flex items-start gap-2.5">
                  <Check
                    size={16}
                    className="mt-0.5 flex-shrink-0"
                    style={{ color: "#10b981" }}
                  />
                  <span className="text-[13.5px]" style={{ color: "var(--fg)" }}>
                    {feat}
                  </span>
                </div>
              ))}
            </div>
            <a
              href="/pricing"
              className="inline-flex items-center gap-1 text-[13px] font-medium mt-2"
              style={{ color: "var(--brand-cyan)" }}
            >
              {t("pricing.detail")}
            </a>
          </div>
        </div>

        <p
          className="text-center text-[11px] font-bold uppercase tracking-[0.2em]"
          style={{ color: "var(--muted)", opacity: 0.6 }}
        >
          {t("pricing.trustedBy")}
        </p>
      </div>
    </section>
  );
};

export default PricingSection;
